import { Avatar } from '@heroui/react';
import type { EProductType } from '@quesmed/types/models';
import type { IDropdownOptionsData } from '@quesmed/types/resolvers/query/admin';
import { DROPDOWN_OPTIONS, type IDropdownOptionsVar } from '@quesmed/types/resolvers/query/admin';
import { useMemo, useState } from 'react';
import { useLazyEffect } from '@/core/hooks';
import { useQuery } from '@/libs';
import { useGlobal } from '@/store';
import { useDebounce } from '../../../hooks/useDebounce';
import { AutoComplete, type AutoCompleteProps } from '../AutoComplete/AutoComplete';
import type { ISelectItem, ISelectItemSection } from '../index';

interface AsyncAutoCompleteProps extends Omit<AutoCompleteProps, 'options'> {
  queryKey?: string;
  extraSections?: ISelectItemSection[];
  extraOptions?: ISelectItem[];
  groupBy?: 'options' | 'sections';
  entitlementIds?: (string | number)[];
  topicIds?: (string | number)[];
  addValuePrefix?: boolean;
  productId?: EProductType;
  shouldUnregister?: boolean;
  apiSearch?: boolean;
  resetOnDbTypeChange?: boolean;
  showAvatar?: boolean;
}

export const AsyncAutoComplete = ({
  queryKey,
  extraSections,
  extraOptions,
  groupBy = 'sections',
  entitlementIds,
  topicIds,
  addValuePrefix = true,
  productId,
  shouldUnregister,
  onSelectionChange,
  selectedKey,
  apiSearch = true,
  resetOnDbTypeChange = false,
  showAvatar = true,
  ...rest
}: AsyncAutoCompleteProps) => {
  const [searchText, setSearchText] = useState('');
  const debouncedSearchText = useDebounce(searchText, 800);
  const dbType = useGlobal.use.dbType();

  const { data, loading, refetch } = useQuery<IDropdownOptionsData, IDropdownOptionsVar>(DROPDOWN_OPTIONS, {
    variables: {
      force: false,
      search: apiSearch ? debouncedSearchText?.trim() : '',
      table: queryKey ?? 'concepts',
      entitlementIds: entitlementIds?.length ? entitlementIds.map(Number) : undefined,
      topicIds: topicIds?.length ? topicIds.map(Number) : undefined,
      productId: productId ? productId : undefined,
    },
  });

  useLazyEffect(() => {
    if (resetOnDbTypeChange) {
      refetch();
    }
  }, [dbType]);

  const sections = useMemo(() => {
    if (groupBy === 'options') {
      return [];
    }
    const opts = data?.admin?.dropdownOptions ?? [];
    const groupedOptions = opts.reduce((acc, opt) => {
      const groupId = opt.metaData?.value ?? opt.value;
      const label = opt.metaData && addValuePrefix ? `[${groupId}] ${opt.metaData.key}` : opt.key;
      if (!groupId) return acc;

      const existingGroup = acc.find((group) => group.label === label);
      const item = {
        key: opt.value as string,
        label: addValuePrefix ? `[${opt.value}] ${opt.key}` : opt.key,
        // description: opt?.description,
      };

      if (existingGroup) {
        existingGroup.items.push(item);
      } else {
        acc.push({ label, items: [item] });
      }

      return acc;
    }, [] as ISelectItemSection[]);

    return [...(extraSections ?? []), ...groupedOptions];
  }, [data, extraSections]);

  const options = useMemo(() => {
    if (groupBy === 'sections') {
      return [];
    }
    const opts = data?.admin?.dropdownOptions ?? [];

    const filteredOptions = opts.map((opt) => ({
      key: opt.value as string,
      label: addValuePrefix ? `[${opt.value}] ${opt.key}` : opt.key,
      description: opt?.description,
    }));

    return [...(extraOptions ?? []), ...filteredOptions];
  }, [data, extraOptions]);

  return (
    <AutoComplete
      {...rest}
      // startContent={
      //   <ChipGroup
      //     transformSelected={(option) => {
      //       return options.find((o) => o.key === option)?.label ?? option;
      //     }}
      //     options={(selectedKey ? [selectedKey] : []) as string[]}
      //   />
      // }
      renderItem={(item) => (
        <div className='flex gap-2 items-center'>
          {showAvatar && (
            <Avatar
              alt={item.label}
              className='shrink-0'
              classNames={{
                base: 'bg-default-200 border-default-400',
                name: 'uppercase',
                // icon: 'text-black/80',
              }}
              name={item.label
                .split(' ')
                .map((word) => word[0])
                .join('')}
              size='sm'
              src={''}
            />
          )}
          <div className='flex flex-col'>
            <span className='text-small'>{item.label}</span>
            {item.description && <span className='text-tiny text-default-400'>{item.description}</span>}
          </div>
        </div>
      )}
      selectedKey={selectedKey != null ? `${selectedKey}` : ''}
      onSelectionChange={onSelectionChange}
      options={groupBy === 'options' ? options : []}
      sections={groupBy === 'sections' ? sections : undefined}
      isLoading={loading}
      onInputChange={setSearchText}
    />
  );
};
